/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

// Those functions are used as helpers for the components that make usage of the form
import isEqual from "lodash.isequal";
import {
  DiscoveryRule,
  RuleOperator,
  GetDiscoveryRulesQuery,
  KeyValue,
} from "~/generated/graphql";
import { isNotNil, isNotEmpty } from "~/tools";
import { ACTION, RuleData } from "./types";

type GetQueryDiscoveryRules = GetDiscoveryRulesQuery["discoveryRules"];

// Helper function for mapping Api response rules to the form input
export function mapQueryRules(
  queryRules: GetQueryDiscoveryRules
): DiscoveryRule[] {
  const rules = queryRules ?? [];

  return rules.filter(isNotNil).map((rule) => {
    const { __typename: ignoredTypename, ...restRule } = rule;

    return {
      ...restRule,
      applicationId: "",
      environmentId: "",
      tagsOperator: RuleOperator.And,
    };
  });
}

function isNotEmptyMatch(match: DiscoveryRule["match"]) {
  const { tags, ...restMatch } = match;
  const hasValues = Object.values(restMatch).some((value) => value != null);

  return isNotEmpty(tags ?? []) || hasValues;
}

export function filterEmptyRules(rules: DiscoveryRule[]) {
  return rules.filter((rule) => isNotEmptyMatch(rule.match));
}

export function getRuleSubmissionDiff(
  previousRules: DiscoveryRule[],
  newRules: DiscoveryRule[]
): RuleData[] {
  const rulesToAdd = getUncommonRules(previousRules, newRules).map((rule) => ({
    action: ACTION.ADD,
    rule,
  }));

  const rulesToRemove = getUncommonRules(newRules, previousRules).map(
    (rule) => ({
      action: ACTION.REMOVE,
      rule,
    })
  );

  const rulesToUpdate = getModifiedRules(
    // Adjust data to match `newRules`, remove empty properties
    mapFormData(previousRules),
    newRules
  ).map((rule) => ({
    action: ACTION.UPDATE,
    rule,
  }));

  return [...rulesToAdd, ...rulesToRemove, ...rulesToUpdate];
}

function getModifiedRules(
  baseRules: DiscoveryRule[],
  newRules: DiscoveryRule[]
) {
  const mappedBaseRules = mapRules(baseRules);

  return mappedBaseRules
    .map((rule) => {
      const common = newRules.find((newRule) => newRule.id === rule.id);

      if (common != null && !isEqual(rule, common)) {
        return {
          ...common,
          id: rule.id,
        };
      }

      return null;
    })
    .filter(isNotNil);
}

function getUncommonRules(
  baseRules: DiscoveryRule[],
  newRules: DiscoveryRule[]
): DiscoveryRule[] {
  return newRules.filter((rule) => {
    return baseRules.every((baseRule) => rule.id !== baseRule.id);
  });
}

export function mapFormData(rules: DiscoveryRule[]): DiscoveryRule[] {
  return rules.map((rule) => {
    const { __typename: ignoredTypename, ...restRule } = rule ?? {};

    return {
      ...restRule,
      match: cleanMatch(restRule.match),
    };
  });
}

function cleanMatch(match: DiscoveryRule["match"]) {
  const { __typename: ignoredTypename, nid: ignoredNid, ...restMatch } = match;
  return {
    ...(restMatch.cloudAccount && { cloudAccount: restMatch.cloudAccount }),
    ...(restMatch.projectId && { projectId: restMatch.projectId }),
    ...(restMatch.resourceGroup && { resourceGroup: restMatch.resourceGroup }),
    ...(restMatch.subnetIds && { subnetIds: restMatch.subnetIds }),
    ...(restMatch.tags &&
      isNotEmpty(restMatch.tags) && {
        tags: cleanTags(restMatch.tags as Tag[]),
      }),
    ...(restMatch.type && {
      type: joinType(restMatch.type, restMatch.subtype ?? []),
    }),
    ...(restMatch.securityGroupIds &&
      isNotEmpty(restMatch.securityGroupIds) && {
        securityGroupIds: restMatch.securityGroupIds,
      }),
  };
}

type Tag = KeyValue & {
  __typename: string;
};

function cleanTags(tags: Tag[]): KeyValue[] {
  return tags.map((tag) => ({
    key: tag.key,
    value: tag.value,
  }));
}

type SplittedTypes = {
  type: string[];
  subtype: string[];
};

export function splitType(type: string[]): SplittedTypes {
  return type.reduce(
    (acc, curr) => {
      const [newType, newSubType] = curr.split("/");

      return {
        type: [...acc.type, newType],
        subtype:
          // Some of resource types don't have `subtype`, e.g. `iam`, `sns`, `project`
          newSubType != null ? [...acc.subtype, newSubType] : acc.subtype,
      };
    },
    {
      type: [] as string[],
      subtype: [] as string[],
    }
  );
}

function joinType(types: string[], subtypes: string[]): string[] {
  return types.reduce((acc, curr, index) => {
    const hasSlash = curr.includes("/");
    const newType = hasSlash ? curr : curr + "/" + subtypes[index];
    return [...acc, newType];
  }, [] as string[]);
}

export function mapRules(rules: DiscoveryRule[]): DiscoveryRule[] {
  return rules.map((rule) => ({
    ...rule,
    match: {
      ...rule.match,
      ...(rule.match.type && splitType(rule.match.type)),
    },
  }));
}
